import { useState, useEffect, useMemo } from "react";
import { apiClient } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, HeartHandshake, Edit2, Trash2, Phone, Mail, MapPin, User, Search, Star, X } from "lucide-react";

export const RELATIONSHIPS = ["Mother", "Father", "Guardian", "Grandparent", "Foster carer", "Other"];

const RELATIONSHIP_COLORS = {
  Mother: "bg-coral-100 text-coral-500",
  Father: "bg-info/10 text-info",
  Guardian: "bg-teal-100 text-teal-700",
  Grandparent: "bg-golden-100 text-golden-500",
  "Foster carer": "bg-success/10 text-success",
};

const EMPTY_FORM = {
  name: "",
  relationship: "Mother",
  phone: "",
  email: "",
  address: "",
  child_ids: [],
  is_primary: false,
  notes: ""
};

export default function CarersPage() {
  const [carers, setCarers] = useState([]);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCarer, setEditingCarer] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [childFilter, setChildFilter] = useState("all");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [carersRes, childrenRes] = await Promise.all([
        apiClient.get("/carers"),
        apiClient.get("/children")
      ]);
      setCarers(carersRes.data || []);
      setChildren(childrenRes.data || []);
    } catch (error) {
      toast.error("Failed to load carers");
    }
    setLoading(false);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return carers.filter(c => {
      if (q && !(c.name || "").toLowerCase().includes(q)) return false;
      if (childFilter !== "all" && !(c.child_ids || []).includes(childFilter)) return false;
      return true;
    });
  }, [carers, search, childFilter]);

  const childName = (id) => children.find(c => c.id === id)?.name;

  const openAdd = () => {
    setEditingCarer(null);
    setForm({ ...EMPTY_FORM, child_ids: childFilter !== "all" ? [childFilter] : [] });
    setDialogOpen(true);
  };

  const openEdit = (carer) => {
    setEditingCarer(carer);
    setForm({
      name: carer.name || "",
      relationship: carer.relationship || "Other",
      phone: carer.phone || "",
      email: carer.email || "",
      address: carer.address || "",
      child_ids: carer.child_ids || [],
      is_primary: !!carer.is_primary,
      notes: carer.notes || ""
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingCarer(null);
    setForm(EMPTY_FORM);
  };

  const toggleChild = (id, checked) => {
    setForm(f => ({
      ...f,
      child_ids: checked ? [...new Set([...f.child_ids, id])] : f.child_ids.filter(x => x !== id)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Full name is required");
      return;
    }
    setSaving(true);
    try {
      if (editingCarer) {
        await apiClient.put(`/carers/${editingCarer.id}`, form);
        toast.success("Carer updated!");
      } else {
        await apiClient.post("/carers", form);
        toast.success("Carer added!");
      }
      closeDialog();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    }
    setSaving(false);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/carers/${deleteTarget.id}`);
      toast.success("Carer deleted");
      setCarers(cs => cs.filter(c => c.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (error) {
      toast.error("Failed to delete");
    }
    setDeleting(false);
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-10 bg-muted rounded-lg w-48" />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => <div key={i} className="h-56 bg-muted rounded-3xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="carers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-primary flex items-center gap-3">
            <HeartHandshake className="h-8 w-8" />
            Carers
          </h1>
          <p className="text-muted-foreground mt-1">Parents and carers of your children</p>
        </div>
        <Button className="rounded-full" onClick={openAdd} data-testid="add-carer-btn">
          <Plus className="mr-2 h-4 w-4" />
          Add Carer
        </Button>
      </div>

      {/* Filters */}
      {carers.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-3" data-testid="carers-filters">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name…"
              className="pl-9 pr-9 rounded-full bg-white"
              data-testid="carers-search-input"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <Select value={childFilter} onValueChange={setChildFilter}>
            <SelectTrigger className="rounded-full bg-white sm:w-56" data-testid="carers-child-filter">
              <SelectValue placeholder="All children" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All children</SelectItem>
              {children.map(child => (
                <SelectItem key={child.id} value={child.id}>{child.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* List */}
      {carers.length === 0 ? (
        <Card className="border-0 shadow-lg rounded-3xl">
          <CardContent className="py-16 text-center">
            <HeartHandshake className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="font-heading text-xl font-bold mb-2">No Carers Yet</h3>
            <p className="text-muted-foreground mb-6">
              Record the parents, guardians and carers involved in your child's care
            </p>
            <Button onClick={openAdd} className="rounded-full" data-testid="empty-add-carer-btn">
              <Plus className="mr-2 h-4 w-4" />
              Add Carer
            </Button>
          </CardContent>
        </Card>
      ) : filtered.length === 0 ? (
        <Card className="border-0 shadow-lg rounded-3xl">
          <CardContent className="py-12 text-center" data-testid="carers-no-results">
            <Search className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-muted-foreground">No carers match your search or filter.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((carer, i) => {
            const linked = (carer.child_ids || []).map(childName).filter(Boolean);
            return (
              <Card
                key={carer.id}
                className={`border-0 shadow-lg rounded-3xl card-hover animate-fadeIn stagger-${Math.min(i + 1, 6)}`}
                data-testid={`carer-card-${carer.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="h-14 w-14 rounded-2xl bg-coral-100 flex items-center justify-center">
                      <HeartHandshake className="h-7 w-7 text-coral-500" />
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEdit(carer)}
                        className="rounded-full h-9 w-9"
                        aria-label={`Edit ${carer.name}`}
                        data-testid={`edit-carer-${carer.id}`}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteTarget(carer)}
                        className="rounded-full h-9 w-9 text-destructive hover:text-destructive"
                        aria-label={`Delete ${carer.name}`}
                        data-testid={`delete-carer-${carer.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <h3 className="font-heading font-bold text-xl mb-1 break-words">{carer.name}</h3>
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <Badge className={`rounded-full ${RELATIONSHIP_COLORS[carer.relationship] || "bg-muted text-muted-foreground"}`}>
                      {carer.relationship}
                    </Badge>
                    {carer.is_primary && (
                      <Badge className="rounded-full bg-primary/10 text-primary" data-testid={`carer-primary-${carer.id}`}>
                        <Star className="h-3 w-3 mr-1 fill-current" />
                        Primary contact
                      </Badge>
                    )}
                  </div>

                  <div className="space-y-2 text-sm text-muted-foreground">
                    <div className="flex items-start gap-2">
                      <User className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <span className="min-w-0">
                        {linked.length ? `For: ${linked.join(", ")}` : "Not linked to a child"}
                      </span>
                    </div>
                    {carer.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 flex-shrink-0" />
                        <a href={`tel:${carer.phone}`} className="hover:text-foreground truncate">{carer.phone}</a>
                      </div>
                    )}
                    {carer.email && (
                      <div className="flex items-center gap-2 min-w-0">
                        <Mail className="h-4 w-4 flex-shrink-0" />
                        <a href={`mailto:${carer.email}`} className="hover:text-foreground truncate">{carer.email}</a>
                      </div>
                    )}
                    {carer.address && (
                      <div className="flex items-center gap-2 min-w-0">
                        <MapPin className="h-4 w-4 flex-shrink-0" />
                        <span className="truncate">{carer.address}</span>
                      </div>
                    )}
                    {carer.notes && (
                      <p className="pt-2 text-xs line-clamp-2 border-t border-border">{carer.notes}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) closeDialog(); }}>
        <DialogContent className="sm:max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="carer-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingCarer ? "Edit Carer" : "Add Carer"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="carer-name">Full Name</Label>
              <Input
                id="carer-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Jane Smith"
                required
                className="rounded-xl"
                data-testid="carer-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Relationship to the child</Label>
              <Select value={form.relationship} onValueChange={(v) => setForm({ ...form, relationship: v })}>
                <SelectTrigger className="rounded-xl" data-testid="carer-relationship-select">
                  <SelectValue placeholder="Select relationship" />
                </SelectTrigger>
                <SelectContent>
                  {RELATIONSHIPS.map(r => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="carer-phone">Phone</Label>
                <Input
                  id="carer-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                  className="rounded-xl"
                  data-testid="carer-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="carer-email">Email</Label>
                <Input
                  id="carer-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="jane@example.com"
                  className="rounded-xl"
                  data-testid="carer-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="carer-address">Address</Label>
              <Input
                id="carer-address"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="12 Oak Street"
                className="rounded-xl"
                data-testid="carer-address-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Linked children</Label>
              {children.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">Add a child profile to link this carer.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="carer-children-list">
                  {children.map(child => {
                    const checked = form.child_ids.includes(child.id);
                    return (
                      <label
                        key={child.id}
                        className={`flex items-center gap-2 p-2.5 rounded-xl border cursor-pointer transition-colors ${checked ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"}`}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(v) => toggleChild(child.id, !!v)}
                          data-testid={`carer-child-checkbox-${child.id}`}
                        />
                        <span className="text-sm font-medium truncate">{child.name}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between p-3 rounded-xl border border-border">
              <div>
                <p className="font-medium text-sm">Primary contact</p>
                <p className="text-xs text-muted-foreground">The main person to contact about the child</p>
              </div>
              <Switch
                checked={form.is_primary}
                onCheckedChange={(v) => setForm({ ...form, is_primary: v })}
                data-testid="carer-primary-switch"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="carer-notes">Notes</Label>
              <Textarea
                id="carer-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Availability, preferred contact method, etc."
                className="rounded-xl resize-none"
                rows={2}
                data-testid="carer-notes-input"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={closeDialog} className="flex-1 rounded-full">
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="flex-1 rounded-full" data-testid="save-carer-btn">
                {saving ? "Saving…" : editingCarer ? "Update" : "Add"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent className="rounded-3xl" data-testid="delete-carer-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This carer's details will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="rounded-full" data-testid="cancel-delete-carer-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={deleting}
              className="rounded-full bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete-carer-btn"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {deleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
